// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { hideAll, Instance as TippyInstance, sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { TippyPopupStyle, tippyPopupStyles } from "@adl/App/Components/Tippy/Style";
import { xHeaderUpdate } from "@adl/App/Handlers/XNG/Header";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getLogoImageWidget } from "@adl/App/Store/Selectors/GeneralSelectors";
import { addClassName, removeClassName } from "@adl/XDL/Utilities";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { injectCss } from "@insite/mobius/utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

const mapDispatchToProps = {
    xHeaderUpdate,
};

export const XngTippy = styled(Tippy)<TippyProps>`
    ${injectCss}
`;

interface TippyPopupProps extends TippyProps {
    closeButton?: "both" | "mobile" | "desktop" | false;
    type?: "menu" | "tooltip";
    mobileMode?: "left-side" | "default" | false;
    onTriggerPopupOpen?: (isOpen: boolean) => void;
    extendedStyles?: TippyPopupStyle;
}

type Props = TippyPopupProps & ResolveThunks<typeof mapDispatchToProps>;

export const defaultTippyProps: TippyProps = {
    placement: "bottom",
    delay: 250,
    maxWidth: "none",
    interactive: true,
    trigger: "mouseenter focus",
    offset: [0, 14],
    animation: "shift-away",
    sticky: "reference",
    plugins: [sticky],
    arrow: true,
    inertia: false,
};

const TippyPopup: FC<Props> = ({
    type,
    mobileMode,
    onTriggerPopupOpen: onTriggerPopupOpenProps,
    closeButton,
    extendedStyles,
    children,
    xHeaderUpdate,
    ...otherProps
}) => {
    const { isTablet, logoImageWidget } = useAppSelector(state => {
        return {
            isTablet: (state as CustomAppState).xCommon.isTabletScreen,
            logoImageWidget: getLogoImageWidget(state),
        };
    });

    const [styles] = useState(() => mergeWithCss(tippyPopupStyles, extendedStyles));

    const [tippyInstance, setTippyInstance] = useState<TippyInstance>();
    const [tippyOptions, setTippyOptions] = useState({});
    const [headerMenuStyle, setHeaderMenuStyle] = useState({
        paddingLeft: 0,
        paddingTop: 0,
        paddingRight: 0,
    });

    const { content: tippyContent, ...tippyProps } = otherProps;

    const applyCssFiles = () => {
        /* eslint-disable global-require */
        require("tippy.js/dist/tippy.css");
        require("tippy.js/animations/shift-away.css");
        /* eslint-enable global-require */
    };

    useEffect(() => {
        applyCssFiles();
    }, []);

    useEffect(() => {
        const tippyOptions = tippyOptionsForMobile(isTablet);
        setTippyOptions(tippyOptions);
    }, [isTablet]);

    const closeButtonHandler = () => {
        tippyInstance?.hide?.();
    };

    const tippyOptionsForMobile = (isMobileDevice?: boolean) => {
        // reset options
        const props = { ...defaultTippyProps, ...otherProps };
        let options = {
            arrow: props.arrow,
            animation: props.animation,
            inertia: props.inertia,
            placement: props.placement,
            trigger: props.trigger,
            maxWidth: props.maxWidth,
        };

        if (isMobileDevice && mobileMode) {
            // These options for default & left-side
            options = {
                ...options,
                trigger: "click",
                arrow: false,
            };

            if (mobileMode === "left-side") {
                options = {
                    ...options,
                    animation: "shift-away",
                    inertia: true,
                    placement: "left-start",
                    maxWidth: 300,
                };
            }
        }

        return options;
    };

    const updateHeaderMenuStyle = (instance: TippyInstance) => {
        const tippyContentElement = tippyInstance?.popper?.querySelector(".tippy-box > .tippy-content");
        if (typeof window !== "undefined" && tippyContentElement) {
            const {
                paddingLeft: paddingLeftStyle,
                paddingTop: paddingTopStyle,
                paddingRight: paddingRightStyle,
            } = window.getComputedStyle(tippyContentElement);
            const paddingLeft = parseInt(paddingLeftStyle || "0", 10);
            const paddingTop = parseInt(paddingTopStyle || "0", 10);
            const paddingRight = parseInt(paddingRightStyle || "0", 10);
            setHeaderMenuStyle({ paddingLeft, paddingTop, paddingRight });
        }
    };

    if (isTablet && !mobileMode) {
        return <span style={styles.elementWrapper}>{children}</span>;
    }

    return (
        <XngTippy
            onCreate={instance => {
                if (mobileMode === "left-side") {
                    instance.popper.className = addClassName(instance.popper.className, "tippy-menu-left-side");
                }
                setTippyInstance(instance);
                tippyProps.onCreate?.(instance);
            }}
            onShow={instance => {
                hideAll(); // hide another TippyPopup
                xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_popup` });
                onTriggerPopupOpenProps?.(true);
                document.querySelector(".prevent-parent-scroll")?.setAttribute("style", "overscroll-behavior: contain");
                tippyProps.onShow?.(instance);
            }}
            onShown={instance => {
                if (type === "menu") {
                    updateHeaderMenuStyle(instance);
                    instance.popper.className = addClassName(instance.popper.className, "menu-opened");
                }
                tippyProps.onShown?.(instance);
            }}
            onHide={instance => {
                if (type === "menu") {
                    instance.popper.className = removeClassName(instance.popper.className, "menu-opened");
                }
                ariaAppHider.show(document.body);
                onTriggerPopupOpenProps?.(false);
                tippyProps.onHide?.(instance);
                document.querySelector(".prevent-parent-scroll")?.removeAttribute("style");
                xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_popup` });
            }}
            content={
                <StyledWrapper className="content-wrapper prevent-parent-scroll">
                    {closeButton && <Icon {...styles.closeButton} {...{ closeButton }} onClick={closeButtonHandler} />}
                    {tippyContent}
                </StyledWrapper>
            }
            {...defaultTippyProps}
            {...tippyOptions}
            {...tippyProps}
            {...styles.main}
        >
            <span style={styles.elementWrapper}>{children}</span>
        </XngTippy>
    );
};

TippyPopup.defaultProps = {
    closeButton: "both",
    mobileMode: "default",
    type: "tooltip",
};

export default connect(null, mapDispatchToProps)(TippyPopup);
